import { useAccountPresentationLocale } from '../../features/account-settings/useAccountPresentationText';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, CircleAlert, type LucideIcon } from 'lucide-react';
import { ToastContext } from './ToastContext';
import type { ToastItem, ToastPushOptions } from './ToastContext';
import './ToastProvider.css';

const TOAST_TONE: Record<Exclude<NonNullable<ToastItem['type']>, 'error'>, {
  icon: LucideIcon;
}> = {
  info: { icon: Info },
  success: { icon: CheckCircle2 },
  warning: { icon: AlertTriangle },
  danger: { icon: CircleAlert },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t: receptionText, language } = useAccountPresentationLocale();

  const [items, setItems] = useState<ToastItem[]>([]);
  const currentItems = useRef(items); currentItems.current = items;
  const dismissed = useRef(new Set<number>());
  const dismiss = useCallback((id: number) => {
    const item = currentItems.current.find(item => item.id === id);
    setItems((current) => current.filter((item) => item.id !== id));
    if (item && !dismissed.current.has(id)) { dismissed.current.add(id); item.onDismiss?.(); }
  }, []);
  useEffect(() => {
    const live = new Set(items.map(item => item.id));
    for (const id of dismissed.current) if (!live.has(id)) dismissed.current.delete(id);
  }, [items]);

  const push = useCallback((msg: string, type: ToastItem['type'] = 'info', ttl = 4000, options?: ToastPushOptions) => {
    const cleanMsg = String(msg || '').trim();
    if (!cleanMsg) return;
    const normalizedType = type === 'error' ? 'danger' : type;

    const id = Date.now() + Math.random();
    setItems((current) => {
      const duplicate = current.some((item) => item.msg === cleanMsg && (item.type ?? 'info') === (normalizedType ?? 'info'));
      if (duplicate) return current;
      const next = [
        ...current,
        {
          id,
          msg: cleanMsg,
          description: options?.description,
          type: normalizedType,
          ttl: Math.max(2500, ttl ?? 4000),
          actionLabel: options?.actionLabel,
          onClick: options?.onClick,
          persistent: options?.persistent,
          onDismiss: options?.onDismiss,
        },
      ];
      if (next.length > 5) {
        const removable = next.findIndex(item => !item.persistent);
        if (removable >= 0) next.splice(removable, 1);
      }
      return next;
    });
    return () => dismiss(id);
  }, [dismiss]);

  const activeItems = items.slice(0, 3);
  const activeItem = activeItems[0] ?? null;

  useEffect(() => {
    if (!activeItem || activeItem.persistent) return undefined;
    const timeoutId = window.setTimeout(() => dismiss(activeItem.id), activeItem.ttl ?? 4000);
    return () => window.clearTimeout(timeoutId);
  }, [activeItem, dismiss]);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      {createPortal(<div className="app-toast-viewport" aria-live="polite" aria-atomic="false" dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language}
        style={{ fontFamily: language === 'ar' ? '"IBM Plex Sans Arabic", "Lexend Deca", "Avenir Next", sans-serif' : '"Lexend Deca", "Avenir Next", sans-serif' }}>
        {activeItems.map((item) => {
          const tone = TOAST_TONE[item.type === 'error' ? 'danger' : item.type ?? 'info'];
          const ToneIcon = tone.icon;
          return (
            <article
              key={item.id}
              className="app-toast"
              data-tone={item.type ?? 'info'}
              role={item.type === 'danger' ? 'alert' : 'status'}
            >
              <span className="app-toast__icon" aria-hidden="true">
                <ToneIcon size={24} strokeWidth={1.7} />
              </span>
              <div className="app-toast__body">
                <div className="app-toast__message">{receptionText(item.msg)}</div>
                {item.description && <div className="app-toast__description">{receptionText(item.description)}</div>}
              </div>
              {item.actionLabel && item.onClick && (
                  <button type="button" className="app-toast__action" onClick={() => {
                    item.onClick?.();
                    dismiss(item.id);
                  }}>
                    {receptionText(item.actionLabel)}
                  </button>
              )}
              <button
                type="button"
                className="app-toast__close"
                aria-label={receptionText("Dismiss notification")}
                onClick={() => dismiss(item.id)}
              >
                <X size={16} />
              </button>
            </article>
          );
        })}
      </div>, document.body)}
    </ToastContext.Provider>
  );
};
